import React, { useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';

import SidebarCategory from './SidebarCategory';
import { logOut } from '../redux/authSlice';
import { setActiveView } from '../redux/viewSlice';
import { collapse, expand } from '../redux/sidebarSlice';
import { clearToken } from '../utilities/auth';
import { authFetch } from '../services/authFetch';
import { msalInstance } from '../msalConfig';
import { prepViewsForDisplay } from '../utilities/views';
import { UserType } from '../constants'

import { getMenuItems } from "../api";

import logoCroppedImage from '../images/sla-logo-cropped.png';
import logOutIcon from '../images/logout.svg';
import leftArrowIcon from '../images/left-arrow.svg';
import expandIcon from '../images/slider-chevron.svg';
import './Sidebar.scss';

function Sidebar(props) {
    const dispatch = useDispatch();

    const { isCollapsed } = useSelector(state => state.sidebar);
    const activeView = useSelector(state => state.view.activeView);
    const userType = useSelector(state => state.auth.userType);

    const [openCategory, setOpenCategory] = useState(null);
    const [views, setViews] = useState({});
    const [viewError, setViewError] = useState(null);
    const [tableauAccountDetails, setTableauAccountDetails] = useState({});
    const [address, setAddress] = useState({});

    const toggleCategory = (name) => {
        if (openCategory === name) {
            setOpenCategory(null);
        }
        else {
            setOpenCategory(name);
        }
    };

    const handleLogOut = useCallback(() => {
        clearToken();

        dispatch(logOut());

        // If an Entra session is active, redirect through MSAL to clear it.
        if (msalInstance.getAllAccounts().length > 0) {
            msalInstance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
        }
    }, [dispatch]);

    useEffect(() => {
        setViewError(null);
        
        getMenuItems({ setViewError, setViews, handleLogOut, setAddress });
    }, [handleLogOut]);

    useEffect(() => {
        setTableauAccountDetails({});

        authFetch(`${window.AnalyticsPortalConfig.apiAddress}/TableauSessions/AccountDetails`)
            .then(result => {
                if (!result.ok) {
                    if (result.status === 401) {
                        throw new Error('401');
                    }

                    throw new Error('Response not OK.');
                }
                
                return result.json();
            })
            .then((result) => {
                setTableauAccountDetails(result);
            })
            .catch((error) => {
                if (error.message === '401') {
                    handleLogOut();

                    return;
                }

                console.error(error);
            });
    }, [handleLogOut]);

    let userBannerTitle = null;

    if (Object.keys(tableauAccountDetails).length > 0 && tableauAccountDetails.isGenericUser) {
        userBannerTitle = "If you believe you should have a non-generic Tableau account, please contact the IT department.";
    }

    return (
        <div id="sidebar-container"
            className={clsx(
                "h-100",
                isCollapsed && "collapsed")}>
            <nav id="sidebar"
                className={clsx(
                    "d-flex flex-column h-100",
                    !isCollapsed && "pt-2")}>
                {isCollapsed &&
                    <button
                        id="sidebar-open-button-2"
                        className="btn flex-grow-1"
                        onClick={() => dispatch(expand())}>
                        <img src={expandIcon} alt="Open Menu" className="svg-image" />
                    </button>
                }
                {!isCollapsed &&
                    <>
                        <div className="d-flex align-items-center pb-2 sidebar-padded">
                            <button
                                id="sidebar-collapse-button"
                                className="btn"
                                onClick={() => dispatch(collapse())}>
                                <img src={leftArrowIcon} alt="Collapse Menu" className="svg-image" />
                            </button>
                            <div className="flex-grow-1 d-flex align-items-center">
                                <button
                                    className="btn btn-block"
                                    onClick={() => dispatch(setActiveView(null))}>
                                    <img
                                        id="sidebar-header-full-logo"
                                        className="mx-auto"
                                        src={logoCroppedImage}
                                        alt="SLA Analytics Logo" />
                                </button>
                            </div>
                        </div>
                        <div className="category-container sidebar-padded">
                            {viewError !== null &&
                                <p className="sidebar-message text-center">{viewError}</p>
                            }
                            {viewError === null && Object.keys(views).length === 0 &&
                                <p className="sidebar-message text-center">Loading Views...</p>
                            }
                            {viewError === null && Object.keys(views).length > 0 && prepViewsForDisplay(views).map(([key, value]) =>
                                <SidebarCategory
                                    key={key}
                                    name={key}
                                    isOpen={openCategory === key}
                                    toggle={toggleCategory}
                                    views={value}
                                    activeView={activeView}
                                    setActiveView={(view) => dispatch(setActiveView(view))} />
                            )}
                        </div>
                    </>
                }
                <div id="sidebar-footer" className="mt-auto py-3 d-flex flex-column align-items-center sidebar-padded">
                    {Object.keys(tableauAccountDetails).length > 0 && !isCollapsed &&
                        <div title={userBannerTitle} className="user-banner">
                            Welcome, {tableauAccountDetails.fullName}!
                        </div>
                    }
                    {!isCollapsed && <>
                        {address.rapid && userType === UserType.SLAUser &&
                            <a href={address.rapid}
                            className="btn link w-100"
                            target="_blank"
                            rel="noopener noreferrer"><FontAwesomeIcon icon={faExternalLinkAlt} /> RAPID</a>
                        }
                        {address.SLIP && userType === UserType.SLIPUser &&
                            <a href={address.SLIP}
                                className="btn link w-100 d-flex align-items-center"
                                target="_blank"
                                rel="noopener noreferrer"><FontAwesomeIcon icon={faExternalLinkAlt} /> SLIP</a>
                        }
                        {address.learningCenter && userType === UserType.SLIPUser &&
                            <a href={address.learningCenter}
                                className="btn link w-100 d-flex align-items-center"
                                target="_blank"
                                rel="noopener noreferrer"><FontAwesomeIcon icon={faExternalLinkAlt} /> Learning Center</a>
                        }
                        {address.slacalWebsite && userType === UserType.SLIPUser &&
                            <a href={address.slacalWebsite}
                                className="btn link w-100 d-flex align-items-center"
                                target="_blank"
                                rel="noopener noreferrer"><FontAwesomeIcon icon={faExternalLinkAlt} /> Website</a>
                        }
                        {userType !== UserType.SLAUser &&
                            <a href="https://www.slacal.com/send-us-a-message"
                                className="btn link w-100 d-flex align-items-center"
                                target="_blank"
                                rel="noopener noreferrer"><FontAwesomeIcon icon={faExternalLinkAlt} /> Contact Us</a>
                        }
                        </>
                    }
                    <button className="btn link w-100" onClick={handleLogOut}>
                        <img src={logOutIcon} alt="Log Out" className="svg-image" />
                        {!isCollapsed &&
                            ` Log Out`
                        }
                    </button>
                </div>
            </nav>
        </div>
    );
}

export default Sidebar;